import { useState } from 'react';
import { Client, ClientType, StatusType, BookkeepingSoftware, defaultClient } from '@/types/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';

interface ClientFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client?: Client | null;
  onSave: (client: Omit<Client, 'id' | 'srNo' | 'createdAt' | 'updatedAt'>) => void;
}

const clientTypes: ClientType[] = ['Individual', 'Company', 'Trust', 'Sole Trader', 'Partnership'];
const statusTypes: StatusType[] = ['Pending', 'In Progress', 'Completed', 'Overdue', 'Not Required'];
const softwareOptions: BookkeepingSoftware[] = ['Xero', 'MYOB', 'QuickBooks', 'Reckon', 'Other', 'None'];

export function ClientForm({ open, onOpenChange, client, onSave }: ClientFormProps) {
  const [formData, setFormData] = useState<Omit<Client, 'id' | 'srNo' | 'createdAt' | 'updatedAt'>>(
    client ? {
      directorName: client.directorName,
      natureOfBusiness: client.natureOfBusiness,
      companyName: client.companyName,
      clientType: client.clientType,
      gstFilling: client.gstFilling,
      abn: client.abn,
      tfn: client.tfn,
      emailAddress: client.emailAddress,
      team: client.team,
      employee: client.employee,
      statementReceived: client.statementReceived,
      basStatus: client.basStatus,
      basReturn: client.basReturn,
      payrollPayslip: client.payrollPayslip,
      stpFile: client.stpFile,
      superPayment: client.superPayment,
      bookkeepingSoftware: client.bookkeepingSoftware,
      handyLedger: client.handyLedger,
      bookkeepingStatus: client.bookkeepingStatus,
      invoices: client.invoices,
      remarks: client.remarks,
    } : defaultClient
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onOpenChange(false);
  };

  const updateField = <K extends keyof typeof formData>(field: K, value: typeof formData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-primary">
            {client ? 'Edit Client' : 'Add New Client'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Basic Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="directorName">Director / Proprietor Name</Label>
                  <Input
                    id="directorName"
                    value={formData.directorName}
                    onChange={(e) => updateField('directorName', e.target.value)}
                    placeholder="Enter name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company / Trust / Sole Trader Name</Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => updateField('companyName', e.target.value)}
                    placeholder="Enter company name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="natureOfBusiness">Nature of Business</Label>
                  <Input
                    id="natureOfBusiness"
                    value={formData.natureOfBusiness}
                    onChange={(e) => updateField('natureOfBusiness', e.target.value)}
                    placeholder="e.g., Retail, IT Services"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientType">Client Type</Label>
                  <Select
                    value={formData.clientType}
                    onValueChange={(value: ClientType) => updateField('clientType', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {clientTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Tax Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Tax Information
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="abn">ABN</Label>
                  <Input
                    id="abn"
                    value={formData.abn}
                    onChange={(e) => updateField('abn', e.target.value)}
                    placeholder="XX XXX XXX XXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tfn">TFN</Label>
                  <Input
                    id="tfn"
                    value={formData.tfn}
                    onChange={(e) => updateField('tfn', e.target.value)}
                    placeholder="XXX XXX XXX"
                  />
                </div>
                <div className="flex items-center space-x-3 pt-7">
                  <Switch
                    id="gstFilling"
                    checked={formData.gstFilling}
                    onCheckedChange={(checked) => updateField('gstFilling', checked)}
                  />
                  <Label htmlFor="gstFilling">GST Registered</Label>
                </div>
              </div>
            </div>

            {/* Contact & Team */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Contact & Team
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="emailAddress">Email Address</Label>
                  <Input
                    id="emailAddress"
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) => updateField('emailAddress', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="team">Team</Label>
                  <Input
                    id="team"
                    value={formData.team}
                    onChange={(e) => updateField('team', e.target.value)}
                    placeholder="Team name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee">Assigned Employee</Label>
                  <Input
                    id="employee"
                    value={formData.employee}
                    onChange={(e) => updateField('employee', e.target.value)}
                    placeholder="Employee name"
                  />
                </div>
              </div>
            </div>

            {/* BAS & Compliance */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                BAS & Compliance Status
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-3">
                  <Switch
                    id="statementReceived"
                    checked={formData.statementReceived}
                    onCheckedChange={(checked) => updateField('statementReceived', checked)}
                  />
                  <Label htmlFor="statementReceived">Statement Received</Label>
                </div>
                <div className="space-y-2">
                  <Label>BAS Status</Label>
                  <Select
                    value={formData.basStatus}
                    onValueChange={(value: StatusType) => updateField('basStatus', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>BAS Return</Label>
                  <Select
                    value={formData.basReturn}
                    onValueChange={(value: StatusType) => updateField('basReturn', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Payroll */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Payroll & Super
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Payroll / Payslip</Label>
                  <Select
                    value={formData.payrollPayslip}
                    onValueChange={(value: StatusType) => updateField('payrollPayslip', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>STP File</Label>
                  <Select
                    value={formData.stpFile}
                    onValueChange={(value: StatusType) => updateField('stpFile', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Super Payment</Label>
                  <Select
                    value={formData.superPayment}
                    onValueChange={(value: StatusType) => updateField('superPayment', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Bookkeeping */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Bookkeeping
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Software</Label>
                  <Select
                    value={formData.bookkeepingSoftware}
                    onValueChange={(value: BookkeepingSoftware) => updateField('bookkeepingSoftware', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {softwareOptions.map(software => (
                        <SelectItem key={software} value={software}>{software}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-3 pt-7">
                  <Switch
                    id="handyLedger"
                    checked={formData.handyLedger}
                    onCheckedChange={(checked) => updateField('handyLedger', checked)}
                  />
                  <Label htmlFor="handyLedger">Handy Ledger</Label>
                </div>
                <div className="space-y-2">
                  <Label>Bookkeeping Status</Label>
                  <Select
                    value={formData.bookkeepingStatus}
                    onValueChange={(value: StatusType) => updateField('bookkeepingStatus', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Invoices Status</Label>
                <Select
                  value={formData.invoices}
                  onValueChange={(value: StatusType) => updateField('invoices', value)}
                >
                  <SelectTrigger className="w-1/3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusTypes.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Remarks */}
            <div className="space-y-2">
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea
                id="remarks"
                value={formData.remarks}
                onChange={(e) => updateField('remarks', e.target.value)}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {client ? 'Update Client' : 'Add Client'}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
